<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

import Footer from "@/components/Footer.vue";
import Phone from "@/components/icons/Phone.vue";
import Message from "@/components/icons/Message.vue";
import Laptop from "@/components/icons/Laptop.vue";

const contactSection = ref<HTMLElement | null>(null);
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

onMounted(() => {
    if (!contactSection.value) {
        return;
    }

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                isVisible.value = true;
                observer?.unobserve(entry.target);
            });
        },
        {
            threshold: 0.05,
            rootMargin: "0px 0px -10% 0px"
        }
    );

    observer.observe(contactSection.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
    <div id="contact" ref="contactSection" :class="{ 'is-visible': isVisible }" class="text-default-white">
        <div class="container mx-auto px-4 pt-8 lg:pt-16 pb-24">
            <h2 class="contact-title text-[2.5rem] lg:text-6xl text-center pb-4 lg:pb-8">KONTAKT</h2>
            <div class="w-28 lg:w-52 h-px bg-default-white/70 mx-auto mb-14"></div>
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 font-light">
                <div class="flex flex-col items-center gap-5">
                    <div class="contact-badge-wrapper">
                    <div class="contact-badge" style="--contact-delay: 0s">
                        <Phone class="text-sandy h-[4.8125rem] w-auto" aria-hidden="true" />
                    </div>
                    </div>
                    <div class="text-center">
                        <a href="tel:+48608410713" class="text-3xl lg:text-[2.5rem] font-medium">+48 608 410 713</a>
                        <div class="text-[1.375rem] lg:text-3xl font-light">W dni robocze, 9:30 - 16:00</div>
                    </div>
                </div>
                <div class="flex flex-col items-center gap-5">
                    <div class="contact-badge-wrapper">
                    <div class="contact-badge" style="--contact-delay: 0.12s">
                        <Message class="text-sandy h-[4.8125rem] w-auto" aria-hidden="true" />
                    </div>
                    </div>
                    <div class="text-center">
                        <a href="mailto:ciao.emozioni@aspe.com.pl" class="text-[1.375rem] lg:text-3xl font-medium custom-break-word">ciao.emozioni@aspe.com.pl</a>
                    </div>
                </div>
                <div class="flex flex-col items-center gap-5">
                    <div class="contact-badge-wrapper">
                    <div class="contact-badge" style="--contact-delay: 0.24s">
                        <Laptop class="text-sandy h-[4.8125rem] w-auto" aria-hidden="true" />
                    </div>
                    </div>
                    <div class="text-center">
                        <a href="https://www.aspe.pl" target="_blank" class="text-[1.375rem] lg:text-3xl font-medium">www.aspe.pl</a>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-default-white">
            <div class="lg:w-1/2 m-auto">
                <Footer/>
            </div>
        </div>
    </div>
</template>

<style scoped>
.contact-title {
    letter-spacing: 4px;
    opacity: 0;
    transform: translateY(16px);
    transition:
        letter-spacing 0.8s ease,
        transform 0.8s ease,
        opacity 0.8s ease;
}

.contact-badge {
    width: 146px;
    height: 146px;
    border-radius: 100%;
    background: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 9px;
    transform: scale(0.9) translateY(24px);
    opacity: 0;
    transition:
        transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) var(--contact-delay),
        opacity 0.5s ease var(--contact-delay);
}

.contact-badge-wrapper {
    border: 3px solid var(--color-default-red);
    border-radius: 100%;
}

#contact.is-visible .contact-title {
    letter-spacing: 10px;
    opacity: 1;
    transform: translateY(0);
}

#contact.is-visible .contact-badge {
    transform: scale(1) translateY(0);
    opacity: 1;
}

.contact-badge:hover {
    transform: scale(1.18) translateY(-4px);
    transition-duration: 0.3s;
}
</style>
